import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { CategoryDot, categoryColor } from '../../components/CategoryTag';
import { comparavel } from '../../components/SeletorDeCategoria';
import { ApiError, api, type CategoryNode } from '../../lib/api';

/**
 * As categorias, todas editaveis.
 *
 * A API ja sabia criar, renomear, recolorir, arquivar e excluir categoria
 * desde a Fase 2 — nao havia tela. Na pratica isso queria dizer que o
 * conjunto que veio de fabrica era o conjunto para sempre, e um sistema de
 * gasto pessoal em que voce nao pode chamar as coisas pelo seu nome e um
 * sistema que voce abandona no segundo mes.
 *
 * Arquivar e excluir sao coisas diferentes, e a tela insiste nisso:
 *
 *   arquivar tira a categoria das listas de escolha e deixa o historico
 *   intacto. E o que se quer em 99% das vezes.
 *
 *   excluir so e permitido quando a categoria nunca foi usada. Apagar uma
 *   categoria com lancamentos deixaria um buraco no relatorio do ano passado,
 *   e o servidor recusa — a tela explica isso antes de a pessoa tentar.
 */

const CORES = ['ink', 'amber', 'green', 'red', 'blue', 'violet', 'teal', 'rose'] as const;

export function CategoriasPage() {
  const cliente = useQueryClient();
  const [busca, setBusca] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const categorias = useQuery({
    queryKey: ['categorias', 'todas'],
    queryFn: () => api.allCategories(),
  });

  const invalidar = () => {
    void cliente.invalidateQueries({ queryKey: ['categorias'] });
    void cliente.invalidateQueries({ queryKey: ['categories'] });
    void cliente.invalidateQueries({ queryKey: ['transactions'] });
    void cliente.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const salvar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Record<string, unknown> }) =>
      api.updateCategory(id, dados),
    onSuccess: () => {
      setErro(null);
      invalidar();
    },
    onError: (falha) => setErro(mensagem(falha)),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => api.deleteCategory(id),
    onSuccess: () => {
      setErro(null);
      invalidar();
    },
    onError: (falha) => setErro(mensagem(falha)),
  });

  const criar = useMutation({
    mutationFn: (dados: Record<string, unknown>) => api.createCategory(dados),
    onSuccess: () => {
      setErro(null);
      invalidar();
    },
    onError: (falha) => setErro(mensagem(falha)),
  });

  const todas = categorias.data?.categories ?? [];
  const saidas = useMemo(() => filtrar(todas, 'EXPENSE', busca), [todas, busca]);
  const entradas = useMemo(() => filtrar(todas, 'INCOME', busca), [todas, busca]);

  const achouAlgo = saidas.length + entradas.length > 0;

  return (
    <div className="stack">
      <header className="between">
        <div>
          <h1>Categorias</h1>
          <p className="card-hint">
            Renomeie, troque a cor, crie as suas. Tudo aqui pode mudar a qualquer momento.
          </p>
        </div>
      </header>

      {erro && <p className="expense">{erro}</p>}

      <section className="card">
        <label htmlFor="busca-categoria">Buscar</label>
        <input
          id="busca-categoria"
          type="search"
          value={busca}
          placeholder="digite parte do nome..."
          onChange={(evento) => setBusca(evento.target.value)}
        />
        {busca.trim() !== '' && !achouAlgo && (
          <p className="muted">Nenhuma categoria com "{busca}".</p>
        )}
      </section>

      <Grupo
        titulo="Saidas"
        descricao="Para onde o dinheiro vai."
        kind="EXPENSE"
        categorias={saidas}
        salvando={salvar.isPending}
        aoSalvar={(id, dados) => salvar.mutate({ id, dados })}
        aoExcluir={(id) => excluir.mutate(id)}
        aoCriar={(dados) => criar.mutate(dados)}
      />

      <Grupo
        titulo="Entradas"
        descricao="De onde o dinheiro vem."
        kind="INCOME"
        categorias={entradas}
        salvando={salvar.isPending}
        aoSalvar={(id, dados) => salvar.mutate({ id, dados })}
        aoExcluir={(id) => excluir.mutate(id)}
        aoCriar={(dados) => criar.mutate(dados)}
      />
    </div>
  );
}

function Grupo({
  titulo,
  descricao,
  kind,
  categorias,
  salvando,
  aoSalvar,
  aoExcluir,
  aoCriar,
}: {
  titulo: string;
  descricao: string;
  kind: 'INCOME' | 'EXPENSE';
  categorias: CategoryNode[];
  salvando: boolean;
  aoSalvar: (id: string, dados: Record<string, unknown>) => void;
  aoExcluir: (id: string) => void;
  aoCriar: (dados: Record<string, unknown>) => void;
}) {
  const [novaEm, setNovaEm] = useState<string | null>(null);

  return (
    <section className="card">
      <div className="between">
        <div>
          <h2 className="card-title">{titulo}</h2>
          <p className="card-hint">{descricao}</p>
        </div>
        <button className="btn ghost" onClick={() => setNovaEm(novaEm === 'raiz' ? null : 'raiz')}>
          Nova categoria
        </button>
      </div>

      {novaEm === 'raiz' && (
        <NovaCategoria
          kind={kind}
          aoCriar={(dados) => {
            aoCriar(dados);
            setNovaEm(null);
          }}
          aoDesistir={() => setNovaEm(null)}
        />
      )}

      {categorias.length === 0 ? (
        <p className="muted">Nenhuma categoria aqui.</p>
      ) : (
        <ul className="cat-lista">
          {categorias.map((mae) => (
            <li key={mae.id}>
              <Linha
                categoria={mae}
                salvando={salvando}
                aoSalvar={aoSalvar}
                aoExcluir={aoExcluir}
                aoAdicionarFilha={() => setNovaEm(novaEm === mae.id ? null : mae.id)}
              />

              {novaEm === mae.id && (
                <NovaCategoria
                  kind={kind}
                  parentId={mae.id}
                  parentName={mae.name}
                  aoCriar={(dados) => {
                    aoCriar(dados);
                    setNovaEm(null);
                  }}
                  aoDesistir={() => setNovaEm(null)}
                />
              )}

              {mae.children.length > 0 && (
                <ul className="cat-filhas">
                  {mae.children.map((filha) => (
                    <li key={filha.id}>
                      <Linha
                        categoria={filha}
                        salvando={salvando}
                        aoSalvar={aoSalvar}
                        aoExcluir={aoExcluir}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Linha({
  categoria,
  salvando,
  aoSalvar,
  aoExcluir,
  aoAdicionarFilha,
}: {
  categoria: CategoryNode;
  salvando: boolean;
  aoSalvar: (id: string, dados: Record<string, unknown>) => void;
  aoExcluir: (id: string) => void;
  aoAdicionarFilha?: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  return (
    <div className={`cat-linha${categoria.archived ? ' arquivada' : ''}`}>
      {editando ? (
        <input
          className="edit-inline"
          autoFocus
          defaultValue={categoria.name}
          maxLength={120}
          disabled={salvando}
          onBlur={(evento) => {
            const nome = evento.target.value.trim();
            if (nome && nome !== categoria.name) aoSalvar(categoria.id, { name: nome });
            setEditando(false);
          }}
          onKeyDown={(evento) => {
            if (evento.key === 'Enter') evento.currentTarget.blur();
            if (evento.key === 'Escape') setEditando(false);
          }}
        />
      ) : (
        <button
          type="button"
          className="cat-nome"
          onClick={() => setEditando(true)}
          title="Clique para renomear"
        >
          <CategoryDot colorToken={categoria.colorToken} />
          <span>{categoria.name}</span>
          {categoria.archived && <span className="badge">arquivada</span>}
        </button>
      )}

      <Cores
        atual={categoria.colorToken}
        aoTrocar={(colorToken) => aoSalvar(categoria.id, { colorToken })}
      />

      <div className="row" style={{ gap: 4 }}>
        {aoAdicionarFilha && (
          <button className="btn ghost" onClick={aoAdicionarFilha} title="Criar uma subcategoria">
            + sub
          </button>
        )}

        <button
          className="btn ghost"
          disabled={salvando}
          onClick={() => aoSalvar(categoria.id, { archived: !categoria.archived })}
          title={
            categoria.archived
              ? 'Voltar a oferecer esta categoria nas listas.'
              : 'Some das listas de escolha. O historico continua inteiro.'
          }
        >
          {categoria.archived ? 'Desarquivar' : 'Arquivar'}
        </button>

        {confirmando ? (
          <>
            <button
              className="btn danger"
              onClick={() => {
                aoExcluir(categoria.id);
                setConfirmando(false);
              }}
            >
              Excluir mesmo
            </button>
            <button className="btn ghost" onClick={() => setConfirmando(false)}>
              Cancelar
            </button>
          </>
        ) : (
          <button
            className="btn ghost"
            onClick={() => setConfirmando(true)}
            title="So funciona se a categoria nunca tiver sido usada. Do contrario, arquive."
          >
            Excluir
          </button>
        )}
      </div>
    </div>
  );
}

function Cores({ atual, aoTrocar }: { atual: string; aoTrocar: (colorToken: string) => void }) {
  return (
    <div className="cat-cores" role="group" aria-label="Cor da categoria">
      {CORES.map((token) => (
        <button
          key={token}
          type="button"
          className={`cat-cor${token === atual ? ' ativa' : ''}`}
          style={{ background: categoryColor(token) }}
          aria-label={`Usar a cor ${token}`}
          aria-pressed={token === atual}
          onClick={() => aoTrocar(token)}
        />
      ))}
    </div>
  );
}

function NovaCategoria({
  kind,
  parentId,
  parentName,
  aoCriar,
  aoDesistir,
}: {
  kind: 'INCOME' | 'EXPENSE';
  parentId?: string;
  parentName?: string;
  aoCriar: (dados: Record<string, unknown>) => void;
  aoDesistir: () => void;
}) {
  const [nome, setNome] = useState('');
  const [cor, setCor] = useState<string>('ink');

  return (
    <div className="cat-nova">
      <input
        className="edit-inline"
        autoFocus
        value={nome}
        maxLength={120}
        placeholder={parentName ? `subcategoria de ${parentName}` : 'nome da categoria'}
        onChange={(evento) => setNome(evento.target.value)}
        onKeyDown={(evento) => {
          if (evento.key === 'Escape') aoDesistir();
          if (evento.key === 'Enter' && nome.trim()) {
            aoCriar({
              name: nome.trim(),
              kind,
              colorToken: cor,
              ...(parentId ? { parentId } : {}),
            });
          }
        }}
      />
      <Cores atual={cor} aoTrocar={setCor} />
      <div className="row" style={{ gap: 4 }}>
        <button
          className="btn"
          disabled={nome.trim() === ''}
          onClick={() =>
            aoCriar({ name: nome.trim(), kind, colorToken: cor, ...(parentId ? { parentId } : {}) })
          }
        >
          Criar
        </button>
        <button className="btn ghost" onClick={aoDesistir}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

/**
 * Filtra pela busca sem perder a hierarquia.
 *
 * Uma mae que casa fica inteira, com as filhas. Uma filha que casa arrasta a
 * mae junto, senao ela apareceria solta e ninguem saberia de onde veio.
 */
function filtrar(
  categorias: CategoryNode[],
  kind: 'INCOME' | 'EXPENSE',
  busca: string,
): CategoryNode[] {
  const alvo = comparavel(busca);
  const doTipo = categorias.filter((categoria) => categoria.kind === kind);
  if (alvo === '') return doTipo;

  return doTipo
    .map((mae) => {
      if (comparavel(mae.name).includes(alvo)) return mae;
      const filhas = mae.children.filter((filha) => comparavel(filha.name).includes(alvo));
      return filhas.length > 0 ? { ...mae, children: filhas } : null;
    })
    .filter((mae): mae is CategoryNode => mae !== null);
}

function mensagem(falha: unknown): string {
  return falha instanceof ApiError ? falha.message : 'Nao consegui salvar.';
}
